import React, { useState, useRef } from "react";
import ReactMarkdown from "react-markdown";
import Form from "react-bootstrap/Form";

/**
 * Toolbar actions
 * `before` is inserted before the selection, `after` after.
 * `block` = true inserts at the start of the line rather than inline.
 */
const TOOLBAR = [
  { icon: "B",  title: "Gras",       before: "**",  after: "**" },
  { icon: "I",  title: "Italique",   before: "_",   after: "_",  style: { fontStyle: "italic" } },
  { icon: "H",  title: "Titre 2",    before: "## ", after: "" },
  { icon: "—",  title: "Séparateur", before: "\n---\n", after: "" },
  { icon: "•",  title: "Liste",      before: "\n- ", after: "" },
  { icon: "1.", title: "Liste numérotée", before: "\n1. ", after: "" },
  { icon: "`",  title: "Code",       before: "`",   after: "`" },
];

const MarkdownEditor = ({
  value = "",
  onChange,
  rows = 4,
  placeholder = "Vous pouvez utiliser le **markdown** pour mettre en forme votre texte…",
  required = false,
  id,
}) => {
  const [activeTab, setActiveTab] = useState("write");
  const textareaRef = useRef(null);

  const applyFormat = (before, after) => {
    const el = textareaRef.current;
    if (!el) return;

    const start = el.selectionStart;
    const end   = el.selectionEnd;
    const selected = value.slice(start, end);
    const newValue =
      value.slice(0, start) + before + selected + after + value.slice(end);

    onChange(newValue);

    // Restore focus + selection after React re-render
    requestAnimationFrame(() => {
      el.focus();
      const cursorStart = start + before.length;
      el.setSelectionRange(cursorStart, cursorStart + selected.length);
    });
  };

  return (
    <div className="border rounded overflow-hidden">
      {/* ── Header: tabs + toolbar ─────────────────────────────────────── */}
      <div
        className="d-flex align-items-center border-bottom px-2"
        style={{
          minHeight: "2rem",
          flexWrap: "wrap",
          gap: "4px",
          backgroundColor: "var(--bs-tertiary-bg)",
        }}
      >
        {/* Tabs */}
        <div className="d-flex me-2" style={{ borderRight: "1px solid var(--bs-border-color)", paddingRight: "8px" }}>
          <button
            type="button"
            className={`btn btn-sm border-0 py-0 px-2 ${activeTab === "write" ? "btn-secondary" : ""}`}
            style={{ 
              fontSize: "0.78rem",
              color: activeTab === "write" ? "var(--bs-body-color)" : "var(--bs-secondary-color)",
              backgroundColor: activeTab === "write" ? "var(--bs-secondary-bg)" : "transparent"
            }}
            onClick={() => setActiveTab("write")}
          >
            Écriture
          </button>
          <button
            type="button"
            className={`btn btn-sm border-0 py-0 px-2 ${activeTab === "preview" ? "btn-secondary" : ""}`}
            style={{ 
              fontSize: "0.78rem",
              color: activeTab === "preview" ? "var(--bs-body-color)" : "var(--bs-secondary-color)",
              backgroundColor: activeTab === "preview" ? "var(--bs-secondary-bg)" : "transparent"
            }}
            onClick={() => setActiveTab("preview")}
          >
            Aperçu
          </button>
        </div>

        {/* Toolbar (visible in write mode only) */}
        {activeTab === "write" && (
          <div className="d-flex flex-wrap" style={{ gap: "2px" }}>
            {TOOLBAR.map((action) => (
              <button
                key={action.title}
                type="button"
                title={action.title}
                className="btn btn-sm btn-outline-secondary border-0 py-0 px-2"
                style={{ 
                  fontSize: "0.8rem", 
                  minWidth: "26px", 
                  color: "var(--bs-body-color)",
                  ...(action.style ?? {}) 
                }}
                onClick={() => applyFormat(action.before, action.after)}
              >
                {action.icon}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* ── Write tab ──────────────────────────────────────────────────── */}
      {activeTab === "write" && (
        <Form.Control
          ref={textareaRef}
          id={id}
          as="textarea"
          rows={rows}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          className="border-0 rounded-0 font-monospace"
          style={{ resize: "vertical", fontSize: "0.875rem" }}
        />
      )}

      {/* ── Preview tab ────────────────────────────────────────────────── */}
      {activeTab === "preview" && (
        <div
          className="p-3 markdown-preview"
          style={{
            minHeight: `${rows * 1.5 + 0.5}rem`,
            backgroundColor: "var(--bs-body-bg)",
            color: "var(--bs-body-color)",
          }}
        >
          {value?.trim() ? (
            <ReactMarkdown>{value}</ReactMarkdown>
          ) : (
            <span className="text-muted fst-italic">Aucun contenu à prévisualiser.</span>
          )}
        </div>
      )}
    </div>
  );
};

export default MarkdownEditor;
